// mirror of auware/.../frontend/src/stores/auth.ts (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations:
//  - Roles are customer / agent / system_admin (NOT auware_admin).
//  - localStorage key `buyist-auth`, refresh cookie `buyist_support_rt`,
//    remember-me cookie `buyist-remember`.
//  - Brand strings updated.

import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { SystemRole } from "@/types/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal representation of the logged-in user. */
export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  role?: SystemRole;
  isSystemAdmin?: boolean;
  isAgent?: boolean;
  mfaEnabled?: boolean;
  hasPasskeys?: boolean;
  isEmailVerified?: boolean;
}

/**
 * JWT token pair returned by the backend on login / refresh.
 *
 * The refresh token's PRIMARY storage is the HttpOnly `buyist_support_rt`
 * cookie set by the backend. The `refreshToken` field on this object
 * remains populated in memory but is NOT persisted to localStorage.
 * Access token is in memory only.
 */
export interface TokenPair {
  accessToken: string;
  /**
   * Refresh token copy held only in memory. Can be empty string if the
   * client was restored via a cookie-only refresh at boot.
   */
  refreshToken: string;
}

/** Shape of the auth store exposed to the rest of the app. */
export interface AuthState {
  /** Currently authenticated user, or `null` if logged out. */
  user: User | null;

  /**
   * Active JWT tokens - in-memory only. Refresh token lives in the
   * HttpOnly `buyist_support_rt` cookie; access token lives here.
   */
  tokens: TokenPair | null;

  /** `true` while we are checking / restoring the session on app boot. */
  isLoading: boolean;

  // -- Actions ---------------------------------------------------------------

  /** Store user + tokens after a successful login. */
  login: (user: User, tokens: TokenPair) => void;

  /**
   * Clear all auth state (logout).
   *
   * Default: also POSTs `/auth/logout` so the server revokes the
   * refresh token in the DB and clears the cookie. Pass
   * `{ serverSide: false }` from error paths that know the cookie is
   * already invalid (e.g. the api.ts 401 interceptor after refresh fails)
   * to avoid a redundant backend call.
   */
  logout: (opts?: { serverSide?: boolean }) => void;

  /** Replace just the token pair (e.g. after a silent refresh). */
  setTokens: (tokens: TokenPair) => void;

  /** Mark the initial session check as complete. */
  setLoading: (loading: boolean) => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const SESSION_MARKER = "buyist-session-active";

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tokens: null,
      isLoading: true,

      login: (user, tokens) => {
        try {
          sessionStorage.setItem(SESSION_MARKER, "1");
        } catch {
          /* noop */
        }
        set({ user, tokens, isLoading: false });
      },

      logout: (opts?: { serverSide?: boolean }) => {
        if (opts?.serverSide !== false) {
          try {
            void fetch("/api/v1/auth/logout", {
              method: "POST",
              credentials: "include",
            });
          } catch {
            /* noop - best-effort */
          }
        }
        try {
          sessionStorage.removeItem(SESSION_MARKER);
        } catch {
          /* noop */
        }
        set({ user: null, tokens: null, isLoading: false });
      },

      setTokens: (tokens) => set({ tokens }),

      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: "buyist-auth",
      // Persist `user` ONLY. Tokens live in memory (access) and in an
      // HttpOnly cookie (refresh). Closes the XSS -> localStorage ->
      // refresh-token exfil vector.
      partialize: (state) => ({
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state?.user) {
          state?.setLoading(false);
          return;
        }

        const rememberCookie = document.cookie.match(
          /(?:^|; )buyist-remember=([^;]*)/,
        );
        const notRemembered = rememberCookie?.[1] === "0";
        const hasSessionMarker =
          sessionStorage.getItem(SESSION_MARKER) === "1";

        if (notRemembered && !hasSessionMarker) {
          state.logout();
          return;
        }
        try {
          sessionStorage.setItem(SESSION_MARKER, "1");
        } catch {
          /* noop */
        }

        // Exchange the HttpOnly cookie for an access token. Browser
        // attaches `buyist_support_rt` automatically via
        // `credentials: "include"`.
        (async () => {
          try {
            const resp = await fetch("/api/v1/auth/refresh", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({}),
            });
            if (!resp.ok) {
              state.logout({ serverSide: false });
              return;
            }
            const data = await resp.json();
            state.setTokens({
              accessToken: data.access_token,
              refreshToken: data.refresh_token ?? "",
            });
          } catch {
            // Network error - keep user, retry via 401 interceptor.
          } finally {
            state.setLoading(false);
          }
        })();
      },
    },
  ),
);

// ---------------------------------------------------------------------------
// Cross-tab auth synchronization
// ---------------------------------------------------------------------------
// Tokens no longer live in localStorage - only `user` does. The storage
// event fires when another tab logs in or out (writing or clearing the
// `user` slot) and we mirror the change into this tab.
//
// Token rotation across tabs is naturally handled by the HttpOnly cookie:
// when Tab A refreshes and the server sets a new cookie, every subsequent
// request from every tab uses the new cookie automatically.
// ---------------------------------------------------------------------------
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "buyist-auth") return;

    if (event.newValue === null) {
      // Another tab cleared auth (logged out).
      // serverSide: false - the other tab already hit /auth/logout.
      useAuthStore.getState().logout({ serverSide: false });
      return;
    }

    try {
      const parsed = JSON.parse(event.newValue);
      const { user } = parsed.state ?? {};
      if (user) {
        useAuthStore.setState({ user });
      } else {
        useAuthStore.getState().logout({ serverSide: false });
      }
    } catch {
      // Malformed storage value - ignore
    }
  });
}
